import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, DollarSign } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { GRADE_INFO } from "@/data/content";
import { getSectionMedia } from "@/lib/api";

const CARDS = [
  {
    key: "like_new",
    bg: "#151A0C",
    accent: "#C6F24E",
    BadgeIcon: Award,
    title: GRADE_INFO.like_new.title,
    lines: ["Nessun segno visibile, come appena uscito dalla scatola.", "Stessa garanzia di 12 mesi, prezzo più basso del nuovo."],
  },
  {
    key: "used",
    bg: "#101208",
    accent: "#9DAF6B",
    BadgeIcon: DollarSign,
    title: GRADE_INFO.used.title,
    lines: ["Lievi segni d'uso, visibili solo in controluce.", "Funzionalità identica e verificata: il miglior prezzo."],
  },
];

// Composizione di gruppo in line-art (iPhone davanti, MacBook dietro, PS5 a
// destra, smartwatch al centro-basso), coerente per entrambe le card. Badge in
// basso a destra: "Come nuovo" = coccarda · "Usato" = tag saldo con %.
function GradeArt({ accent, bg }) {
  const stroke = { stroke: accent, strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  const soft = { fill: accent, fillOpacity: 0.14 };
  const screen = { fill: accent, fillOpacity: 0.05 };
  const occl = { fill: bg }; // riempimento opaco: i device davanti coprono quelli dietro
  return (
    <div className="relative flex aspect-[240/190] w-full max-w-[300px] shrink-0 items-center justify-center sm:w-[300px]">
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 60% at 50% 48%, ${accent}22, transparent 72%)` }} />
      <svg viewBox="0 0 240 190" fill="none" className="relative h-full w-auto">
        {/* MacBook (dietro, centro) */}
        <g {...stroke}>
          <rect x="64" y="14" width="116" height="72" rx="6" {...screen} />
          <rect x="116" y="14" width="12" height="3.5" rx="1" {...soft} stroke="none" />
          <path d="M58 87 H186" strokeWidth="2" />
          <path d="M54 88 H190 L200 106 H44 Z" {...screen} />
          <path d="M115 88 a7 3 0 0 0 14 0" />
        </g>

        {/* PS5 (destra) — davanti al portatile */}
        <g {...stroke}>
          <path d="M198 48 C213 53 216 66 212 79 C210 85 206 87 206 90 C206 93 210 95 212 104 C216 120 213 134 198 138 Z" {...occl} />
          <path d="M190 48 C175 53 172 66 176 79 C178 85 182 87 182 90 C182 93 178 95 176 104 C172 120 175 134 190 138 Z" {...occl} />
          <rect x="192" y="52" width="6" height="84" {...soft} stroke="none" />
          <path d="M182 140 H206" strokeWidth="1.8" />
        </g>

        {/* iPhone (davanti, sinistra) */}
        <g {...stroke}>
          <rect x="14" y="46" width="54" height="122" rx="15" {...occl} />
          <rect x="33" y="53" width="20" height="6" rx="3" {...soft} stroke="none" />
          <path d="M12 74 v14" />
          <path d="M70 80 v20" />
        </g>

        {/* Smartwatch (davanti, centro-basso) */}
        <g {...stroke}>
          <path d="M107 96 L131 96 L128 112 L110 112 Z" {...occl} />
          <path d="M110 152 L128 152 L131 170 L107 170 Z" {...occl} />
          <rect x="103" y="110" width="32" height="44" rx="12" {...occl} />
          <rect x="135" y="126" width="5" height="8" rx="2" {...soft} stroke="none" />
        </g>
      </svg>
    </div>
  );
}

// Come nuovo / Usato — due card, differenza di temperatura come segnale. Nessun prezzo.
export function TwoGrades() {
  const [media, setMedia] = useState({});
  useEffect(() => {
    getSectionMedia().then((d) => setMedia(d?.grades || {})).catch(() => {});
  }, []);
  return (
    <section className="section-tight" data-testid="home-two-grades">
      <div className="container-page">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {CARDS.map((c, i) => {
            const m = media[c.key] || {};
            return (
            <Reveal
              key={c.key}
              delay={i * 0.08}
              className="flex flex-col gap-6 rounded-2xl p-8 sm:flex-row sm:items-center lg:p-10"
              style={{ backgroundColor: c.bg }}
              data-testid={`grade-block-${c.key}`}
            >
              {m.image ? (
                <div className="relative flex aspect-[240/190] w-full max-w-[300px] shrink-0 items-center justify-center overflow-hidden sm:w-[300px]">
                  <img
                    src={m.image}
                    alt={c.title}
                    className="max-h-full max-w-full object-contain"
                    style={{ transform: `scale(${Number(m.scale) || 1})`, objectPosition: `${m.pos_x ?? 50}% ${m.pos_y ?? 50}%` }}
                  />
                </div>
              ) : (
                <GradeArt accent={c.accent} bg={c.bg} />
              )}
              <div>
                <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-lime text-ink shadow-[0_0_0_5px_rgba(198,242,78,0.14)]" aria-hidden="true">
                  <c.BadgeIcon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display text-2xl font-extrabold tracking-tight text-foreground">{c.title}</h3>
                {c.lines.map((l) => (
                  <p key={l} className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{l}</p>
                ))}
              </div>
            </Reveal>
            );
          })}
        </div>
        <div className="mt-6">
          <Link to="/garanzia" className="text-sm font-semibold text-lime" data-testid="grades-how-link">
            Come valutiamo le condizioni →
          </Link>
        </div>
      </div>
    </section>
  );
}
